import React, { useState, useEffect, useCallback } from 'react';
import {
  IoShieldCheckmarkOutline, IoClose, IoAddOutline, IoTrashOutline,
  IoCreateOutline, IoEyeOutline, IoEyeOffOutline, IoCheckmarkCircle,
  IoChevronDown, IoChevronUp, IoCallOutline, IoShareOutline,
} from 'react-icons/io5';

const ICON_OPTIONS = ['🔒', '👁️', '🏠', '💼', '🏥', '🎭', '👪', '🌍', '🔐', '🛡️'];

export default function PrivacyPanel({ token, api, onClose }) {
  const [profiles, setProfiles] = useState([]);
  const [metadata, setMetadata] = useState({ labels: [], categories: [], total_memories: 0 });
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [tierStats, setTierStats] = useState(null);
  const [defaultTier, setDefaultTier] = useState(3);
  const [tierSaving, setTierSaving] = useState(false);

  // Form state
  const [form, setForm] = useState({
    name: '', description: '', icon: '🔒',
    is_default_share: false, is_default_phone: false,
    rules: {
      mode: 'blocklist', labels: [], categories: [],
      date_after: '', date_before: '',
      memory_ids: [], keywords_blocked: [],
    },
  });

  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  const fetchProfiles = useCallback(async () => {
    if (!token || !api) return;
    try {
      const res = await fetch(`${api}/privacy/profiles`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) {
        const data = await res.json();
        setProfiles(data.profiles || []);
      }
    } catch (e) { console.error('Error fetching privacy profiles:', e); }
  }, [token, api]);

  const fetchMetadata = useCallback(async () => {
    if (!token || !api) return;
    try {
      const res = await fetch(`${api}/privacy/memory-metadata`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) {
        const data = await res.json();
        setMetadata(data);
      }
    } catch (e) { console.error('Error fetching metadata:', e); }
  }, [token, api]);

  const fetchTierStats = useCallback(async () => {
    if (!token || !api) return;
    try {
      const res = await fetch(`${api}/privacy/tiers`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) {
        const data = await res.json();
        setTierStats(data);
        setDefaultTier(data.default_tier || 3);
      }
    } catch (e) { console.error('Error fetching tier stats:', e); }
  }, [token, api]);

  const handleSetDefaultTier = async (tier) => {
    setTierSaving(true);
    try {
      const res = await fetch(`${api}/privacy/tiers/default`, {
        method: 'PUT', headers,
        body: JSON.stringify({ tier }),
      });
      if (res.ok) {
        setDefaultTier(tier);
        setSuccess('Default tier updated');
        setTimeout(() => setSuccess(''), 2000);
      }
    } catch (e) { setError(`Error: ${e.message}`); }
    finally { setTierSaving(false); }
  };

  useEffect(() => {
    Promise.all([fetchProfiles(), fetchMetadata(), fetchTierStats()]).finally(() => setLoading(false));
  }, [fetchProfiles, fetchMetadata, fetchTierStats]);

  const resetForm = () => {
    setForm({
      name: '', description: '', icon: '🔒',
      is_default_share: false, is_default_phone: false,
      rules: { mode: 'blocklist', labels: [], categories: [], date_after: '', date_before: '', memory_ids: [], keywords_blocked: [] },
    });
    setEditingId(null);
    setShowCreate(false);
    setPreview(null);
  };

  const startEdit = (profile) => {
    setForm({
      name: profile.name,
      description: profile.description || '',
      icon: profile.icon || '🔒',
      is_default_share: profile.is_default_share || false,
      is_default_phone: profile.is_default_phone || false,
      rules: {
        mode: profile.rules?.mode || 'blocklist',
        labels: profile.rules?.labels || [],
        categories: profile.rules?.categories || [],
        date_after: profile.rules?.date_after || '',
        date_before: profile.rules?.date_before || '',
        memory_ids: profile.rules?.memory_ids || [],
        keywords_blocked: profile.rules?.keywords_blocked || [],
      },
    });
    setEditingId(profile.id);
    setShowCreate(true);
    setPreview(null);
  };

  const handleSave = async () => {
    if (!form.name.trim()) { setError('Name is required'); return; }
    setError('');
    const cleanRules = { ...form.rules };
    if (!cleanRules.date_after) delete cleanRules.date_after;
    if (!cleanRules.date_before) delete cleanRules.date_before;
    if (!cleanRules.labels.length) delete cleanRules.labels;
    if (!cleanRules.categories.length) delete cleanRules.categories;
    if (!cleanRules.memory_ids.length) delete cleanRules.memory_ids;
    if (!cleanRules.keywords_blocked.length) delete cleanRules.keywords_blocked;

    const body = { ...form, rules: cleanRules };
    try {
      let res;
      if (editingId) {
        res = await fetch(`${api}/privacy/profiles/${editingId}`, { method: 'PUT', headers, body: JSON.stringify(body) });
      } else {
        res = await fetch(`${api}/privacy/profiles`, { method: 'POST', headers, body: JSON.stringify(body) });
      }
      if (res.ok) {
        setSuccess(editingId ? 'Profile updated' : 'Profile created');
        setTimeout(() => setSuccess(''), 3000);
        resetForm();
        fetchProfiles();
      } else {
        const data = await res.json();
        setError(data.detail || 'Failed to save');
      }
    } catch (e) { setError(`Error: ${e.message}`); }
  };

  const handleDelete = async (id) => {
    try {
      const res = await fetch(`${api}/privacy/profiles/${id}`, { method: 'DELETE', headers });
      if (res.ok) {
        setSuccess('Profile deleted');
        setTimeout(() => setSuccess(''), 3000);
        fetchProfiles();
      }
    } catch (e) { setError(`Error: ${e.message}`); }
  };

  const handlePreview = async () => {
    setPreviewLoading(true);
    try {
      const res = await fetch(`${api}/privacy/preview`, {
        method: 'POST', headers,
        body: JSON.stringify({ rules: form.rules }),
      });
      if (res.ok) {
        const data = await res.json();
        setPreview(data);
      }
    } catch (e) { console.error(e); }
    finally { setPreviewLoading(false); }
  };

  const toggleInList = (list, item) =>
    list.includes(item) ? list.filter(i => i !== item) : [...list, item];

  const updateRules = (key, value) =>
    setForm(f => ({ ...f, rules: { ...f.rules, [key]: value } }));

  const [kwInput, setKwInput] = useState('');

  // Styles
  const panel = {
    position: 'fixed', top: 0, right: 0, bottom: 0, width: 380, maxWidth: '100vw',
    background: '#1a1f16', borderLeft: '1px solid rgba(255,255,255,0.08)',
    zIndex: 1100, display: 'flex', flexDirection: 'column',
    color: '#e0ddd5', fontFamily: "'Inter', sans-serif",
    boxShadow: '-8px 0 32px rgba(0,0,0,0.4)',
  };
  const header = {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '16px 20px', borderBottom: '1px solid rgba(255,255,255,0.08)',
  };
  const scrollArea = { flex: 1, overflowY: 'auto', padding: '16px 20px' };
  const chip = (active) => ({
    display: 'inline-flex', alignItems: 'center', gap: 4,
    padding: '4px 10px', borderRadius: 12, fontSize: 11, cursor: 'pointer',
    background: active ? 'rgba(59,114,95,0.25)' : 'rgba(255,255,255,0.05)',
    border: `1px solid ${active ? 'rgba(59,114,95,0.5)' : 'rgba(255,255,255,0.08)'}`,
    color: active ? '#6dbc9a' : '#999', fontWeight: active ? 600 : 400,
    transition: 'all 0.15s',
  });
  const inputStyle = {
    fontSize: 12, padding: '6px 10px', borderRadius: 6,
    background: 'rgba(255,255,255,0.06)', color: '#fff',
    border: '1px solid rgba(255,255,255,0.12)', outline: 'none',
    width: '100%', boxSizing: 'border-box',
  };
  const btnPrimary = {
    fontSize: 12, padding: '8px 16px', borderRadius: 6, cursor: 'pointer',
    background: 'rgba(59,114,95,0.2)', color: '#6dbc9a',
    border: '1px solid rgba(59,114,95,0.4)', fontWeight: 600,
  };
  const btnDanger = {
    fontSize: 11, padding: '4px 10px', borderRadius: 4, cursor: 'pointer',
    background: 'rgba(255,80,80,0.1)', color: '#f66',
    border: '1px solid rgba(255,80,80,0.2)',
  };
  const sectionTitle = {
    fontSize: 10, fontWeight: 700, textTransform: 'uppercase',
    letterSpacing: '0.8px', color: 'rgba(255,255,255,0.4)', marginBottom: 8, marginTop: 14,
  };

  return (
    <div style={panel}>
      {/* Header */}
      <div style={header}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <IoShieldCheckmarkOutline style={{ fontSize: 20, color: '#6dbc9a' }} />
          <span style={{ fontSize: 16, fontWeight: 700 }}>Privacy</span>
          <span style={{ fontSize: 11, opacity: 0.4 }}>{metadata.total_memories} memories</span>
        </div>
        <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#999', fontSize: 18 }}>
          <IoClose />
        </button>
      </div>

      {/* Messages */}
      {error && <div style={{ padding: '8px 20px', fontSize: 11, color: '#f66', background: 'rgba(255,80,80,0.08)' }}>{error}</div>}
      {success && <div style={{ padding: '8px 20px', fontSize: 11, color: '#4caf50', background: 'rgba(76,175,80,0.08)' }}>{success}</div>}

      <div style={scrollArea}>
        {loading ? (
          <div style={{ textAlign: 'center', opacity: 0.5, padding: 40 }}>Loading...</div>
        ) : showCreate ? (
          /* ═══ CREATE / EDIT FORM ═══ */
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <span style={{ fontSize: 14, fontWeight: 600 }}>{editingId ? 'Edit Profile' : 'New Privacy Profile'}</span>
              <button onClick={resetForm} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#999', fontSize: 12 }}>Cancel</button>
            </div>

            {/* Icon picker */}
            <div style={sectionTitle}>Icon</div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 8 }}>
              {ICON_OPTIONS.map(ic => (
                <button key={ic} onClick={() => setForm(f => ({ ...f, icon: ic }))}
                  style={{ fontSize: 18, padding: '4px 8px', borderRadius: 6, cursor: 'pointer',
                    background: form.icon === ic ? 'rgba(59,114,95,0.25)' : 'rgba(255,255,255,0.04)',
                    border: form.icon === ic ? '1px solid rgba(59,114,95,0.5)' : '1px solid transparent',
                  }}>{ic}</button>
              ))}
            </div>

            {/* Name + Description */}
            <div style={sectionTitle}>Name</div>
            <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
              placeholder="e.g. Family Sharing, Work, Public" style={inputStyle} />

            <div style={sectionTitle}>Description</div>
            <input value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
              placeholder="Optional description" style={inputStyle} />

            {/* Mode toggle */}
            <div style={sectionTitle}>Filter Mode</div>
            <div style={{ display: 'flex', gap: 6, marginBottom: 4 }}>
              {[{ val: 'blocklist', label: 'Block matching', desc: 'Share everything EXCEPT selected' },
                { val: 'allowlist', label: 'Allow matching', desc: 'ONLY share selected memories' }].map(m => (
                <button key={m.val}
                  onClick={() => updateRules('mode', m.val)}
                  style={{
                    flex: 1, padding: '8px 6px', borderRadius: 6, cursor: 'pointer', textAlign: 'center',
                    background: form.rules.mode === m.val ? 'rgba(59,114,95,0.2)' : 'rgba(255,255,255,0.03)',
                    border: `1px solid ${form.rules.mode === m.val ? 'rgba(59,114,95,0.4)' : 'rgba(255,255,255,0.08)'}`,
                    color: form.rules.mode === m.val ? '#6dbc9a' : '#888',
                  }}>
                  <div style={{ fontSize: 11, fontWeight: 600, marginBottom: 2 }}>
                    {form.rules.mode === m.val ? <IoEyeOffOutline style={{ marginRight: 4 }} /> : <IoEyeOutline style={{ marginRight: 4 }} />}
                    {m.label}
                  </div>
                  <div style={{ fontSize: 9, opacity: 0.6 }}>{m.desc}</div>
                </button>
              ))}
            </div>

            {/* Labels filter */}
            {metadata.labels.length > 0 && (
              <>
                <div style={sectionTitle}>
                  Labels {form.rules.mode === 'blocklist' ? '(blocked)' : '(allowed)'}
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                  {metadata.labels.map(label => (
                    <span key={label} style={chip(form.rules.labels.includes(label))}
                      onClick={() => updateRules('labels', toggleInList(form.rules.labels, label))}>
                      {label}
                    </span>
                  ))}
                </div>
              </>
            )}

            {/* Categories filter */}
            {metadata.categories.length > 0 && (
              <>
                <div style={sectionTitle}>
                  Categories {form.rules.mode === 'blocklist' ? '(blocked)' : '(allowed)'}
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                  {metadata.categories.map(cat => (
                    <span key={cat} style={chip(form.rules.categories.includes(cat))}
                      onClick={() => updateRules('categories', toggleInList(form.rules.categories, cat))}>
                      {cat}
                    </span>
                  ))}
                </div>
              </>
            )}

            {/* Date range */}
            <div style={sectionTitle}>Date Range</div>
            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              <input type="date" value={form.rules.date_after || ''} onChange={e => updateRules('date_after', e.target.value)}
                style={{ ...inputStyle, flex: 1, colorScheme: 'dark' }} />
              <span style={{ fontSize: 11, opacity: 0.4 }}>to</span>
              <input type="date" value={form.rules.date_before || ''} onChange={e => updateRules('date_before', e.target.value)}
                style={{ ...inputStyle, flex: 1, colorScheme: 'dark' }} />
            </div>

            {/* Keyword blocklist */}
            <div style={sectionTitle}>Blocked Keywords</div>
            <div style={{ fontSize: 10, opacity: 0.4, marginBottom: 4 }}>Memories containing these words will always be hidden</div>
            <div style={{ display: 'flex', gap: 4, marginBottom: 6 }}>
              <input value={kwInput} onChange={e => setKwInput(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter' && kwInput.trim()) {
                    updateRules('keywords_blocked', [...form.rules.keywords_blocked, kwInput.trim().toLowerCase()]);
                    setKwInput('');
                  }
                }}
                placeholder="Type a keyword and press Enter" style={{ ...inputStyle, flex: 1 }} />
            </div>
            {form.rules.keywords_blocked.length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 6 }}>
                {form.rules.keywords_blocked.map((kw, i) => (
                  <span key={i} style={{ ...chip(true), color: '#f66', background: 'rgba(255,80,80,0.1)', borderColor: 'rgba(255,80,80,0.3)' }}>
                    {kw}
                    <IoClose style={{ cursor: 'pointer', fontSize: 10 }}
                      onClick={() => updateRules('keywords_blocked', form.rules.keywords_blocked.filter((_, j) => j !== i))} />
                  </span>
                ))}
              </div>
            )}

            {/* Default toggles */}
            <div style={sectionTitle}>Defaults</div>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, cursor: 'pointer', marginBottom: 6 }}>
              <input type="checkbox" checked={form.is_default_share}
                onChange={e => setForm(f => ({ ...f, is_default_share: e.target.checked }))} />
              <IoShareOutline style={{ fontSize: 14 }} />
              Default for Share Links
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, cursor: 'pointer', marginBottom: 10 }}>
              <input type="checkbox" checked={form.is_default_phone}
                onChange={e => setForm(f => ({ ...f, is_default_phone: e.target.checked }))} />
              <IoCallOutline style={{ fontSize: 14 }} />
              Default for Phone Calls
            </label>

            {/* Preview */}
            <button onClick={handlePreview} disabled={previewLoading}
              style={{ ...btnPrimary, width: '100%', marginBottom: 6, textAlign: 'center', opacity: previewLoading ? 0.5 : 1 }}>
              <IoEyeOutline style={{ marginRight: 4 }} />
              {previewLoading ? 'Calculating...' : 'Preview Impact'}
            </button>
            {preview && (
              <div style={{ padding: 10, borderRadius: 6, background: 'rgba(59,114,95,0.1)', border: '1px solid rgba(59,114,95,0.2)', marginBottom: 10, fontSize: 12 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span>Total memories:</span><strong>{preview.total_memories}</strong>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', color: '#6dbc9a' }}>
                  <span><IoEyeOutline style={{ marginRight: 4 }} />Visible:</span><strong>{preview.visible_memories}</strong>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', color: '#f66' }}>
                  <span><IoEyeOffOutline style={{ marginRight: 4 }} />Hidden:</span><strong>{preview.hidden_memories}</strong>
                </div>
              </div>
            )}

            {/* Save */}
            <button onClick={handleSave} style={{ ...btnPrimary, width: '100%', textAlign: 'center' }}>
              <IoCheckmarkCircle style={{ marginRight: 4 }} />
              {editingId ? 'Save Changes' : 'Create Profile'}
            </button>
          </div>
        ) : (
          /* ═══ PROFILES LIST ═══ */
          <div>
            {/* ── Five-Tier Privacy (PRD 3.1) ── */}
            {tierStats && (
              <div style={{ marginBottom: 16, padding: 12, borderRadius: 8, background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)' }}>
                <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 10, display: 'flex', alignItems: 'center', gap: 6 }}>
                  <IoShieldCheckmarkOutline style={{ color: '#6dbc9a' }} />
                  Memory Privacy Tiers
                </div>
                {[
                  { tier: 1, label: 'Public', color: '#4caf50', desc: 'Anyone can see' },
                  { tier: 2, label: 'Community', color: '#8bc34a', desc: 'Share links' },
                  { tier: 3, label: 'Inner Circle', color: '#e8c77b', desc: 'Trusted shares' },
                  { tier: 4, label: 'Family Only', color: '#ff9800', desc: 'Family members' },
                  { tier: 5, label: 'Private Vault', color: '#f44336', desc: 'Only you' },
                ].map(t => {
                  const count = tierStats.tiers?.[t.tier] || 0;
                  const pct = tierStats.total > 0 ? (count / tierStats.total) * 100 : 0;
                  return (
                    <div key={t.tier} style={{ marginBottom: 6 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 11, marginBottom: 2 }}>
                        <span style={{ color: t.color, fontWeight: 600 }}>
                          {t.tier}. {t.label}
                          <span style={{ fontWeight: 400, opacity: 0.5, marginLeft: 4 }}>{t.desc}</span>
                        </span>
                        <span style={{ opacity: 0.6 }}>{count}</span>
                      </div>
                      <div style={{ height: 4, borderRadius: 2, background: 'rgba(255,255,255,0.06)' }}>
                        <div style={{ height: '100%', borderRadius: 2, background: t.color, width: `${pct}%`, transition: 'width 0.3s' }} />
                      </div>
                    </div>
                  );
                })}

                <div style={{ marginTop: 12, paddingTop: 10, borderTop: '1px solid rgba(255,255,255,0.06)' }}>
                  <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.8px', color: 'rgba(255,255,255,0.4)', marginBottom: 6 }}>
                    Default Tier for New Memories
                  </div>
                  <div style={{ display: 'flex', gap: 4 }}>
                    {[
                      { tier: 1, label: 'Public', color: '#4caf50' },
                      { tier: 2, label: 'Community', color: '#8bc34a' },
                      { tier: 3, label: 'Inner Circle', color: '#e8c77b' },
                      { tier: 4, label: 'Family', color: '#ff9800' },
                      { tier: 5, label: 'Vault', color: '#f44336' },
                    ].map(t => (
                      <button key={t.tier} onClick={() => handleSetDefaultTier(t.tier)} disabled={tierSaving}
                        style={{
                          flex: 1, padding: '5px 2px', borderRadius: 5, cursor: 'pointer', fontSize: 9,
                          fontWeight: defaultTier === t.tier ? 700 : 400, textAlign: 'center',
                          background: defaultTier === t.tier ? `${t.color}22` : 'rgba(255,255,255,0.03)',
                          border: `1px solid ${defaultTier === t.tier ? t.color : 'rgba(255,255,255,0.08)'}`,
                          color: defaultTier === t.tier ? t.color : '#888',
                          transition: 'all 0.15s',
                        }}>
                        {t.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}

            <div style={{ fontSize: 12, opacity: 0.5, marginBottom: 12, lineHeight: 1.5 }}>
              Privacy Profiles control which memories are shared via Share Links and Phone Calls.
              Create profiles to manage what others can see.
            </div>

            <button onClick={() => setShowCreate(true)}
              style={{ ...btnPrimary, width: '100%', textAlign: 'center', marginBottom: 16, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
              <IoAddOutline /> New Privacy Profile
            </button>

            {profiles.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 30, opacity: 0.4, fontSize: 12 }}>
                <IoShieldCheckmarkOutline style={{ fontSize: 32, marginBottom: 8, display: 'block', margin: '0 auto 8px' }} />
                No privacy profiles yet.<br />All memories are shared by default.
              </div>
            ) : (
              profiles.map(p => (
                <ProfileCard key={p.id} profile={p}
                  onEdit={() => startEdit(p)}
                  onDelete={() => handleDelete(p.id)} />
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function ProfileCard({ profile, onEdit, onDelete }) {
  const [expanded, setExpanded] = useState(false);
  const rules = profile.rules || {};
  const hasLabels = rules.labels?.length > 0;
  const hasCategories = rules.categories?.length > 0;
  const hasKeywords = rules.keywords_blocked?.length > 0;
  const hasDateRange = rules.date_after || rules.date_before;
  const ruleCount = (hasLabels ? 1 : 0) + (hasCategories ? 1 : 0) + (hasKeywords ? 1 : 0) + (hasDateRange ? 1 : 0);

  return (
    <div style={{
      marginBottom: 8, borderRadius: 8,
      background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)',
      overflow: 'hidden',
    }}>
      <div onClick={() => setExpanded(!expanded)}
        style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', cursor: 'pointer' }}>
        <span style={{ fontSize: 20 }}>{profile.icon}</span>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 13, fontWeight: 600 }}>{profile.name}</div>
          <div style={{ fontSize: 10, opacity: 0.4, display: 'flex', gap: 8, marginTop: 2 }}>
            <span>{rules.mode === 'allowlist' ? 'Allow-only' : 'Block'} · {ruleCount} rule{ruleCount !== 1 ? 's' : ''}</span>
            {profile.is_default_share && <span style={{ color: '#6dbc9a' }}><IoShareOutline /> Share</span>}
            {profile.is_default_phone && <span style={{ color: '#e8c77b' }}><IoCallOutline /> Phone</span>}
          </div>
        </div>
        {expanded ? <IoChevronUp style={{ opacity: 0.4 }} /> : <IoChevronDown style={{ opacity: 0.4 }} />}
      </div>

      {expanded && (
        <div style={{ padding: '0 12px 10px', fontSize: 11 }}>
          {profile.description && <div style={{ opacity: 0.5, marginBottom: 6 }}>{profile.description}</div>}

          {hasLabels && (
            <div style={{ marginBottom: 4 }}>
              <span style={{ opacity: 0.4, fontSize: 10 }}>Labels:</span>{' '}
              {rules.labels.map(l => <span key={l} style={{ color: '#6dbc9a', marginRight: 4 }}>{l}</span>)}
            </div>
          )}
          {hasCategories && (
            <div style={{ marginBottom: 4 }}>
              <span style={{ opacity: 0.4, fontSize: 10 }}>Categories:</span>{' '}
              {rules.categories.map(c => <span key={c} style={{ color: '#bb86fc', marginRight: 4 }}>{c}</span>)}
            </div>
          )}
          {hasKeywords && (
            <div style={{ marginBottom: 4 }}>
              <span style={{ opacity: 0.4, fontSize: 10 }}>Blocked keywords:</span>{' '}
              {rules.keywords_blocked.map(k => <span key={k} style={{ color: '#f66', marginRight: 4 }}>{k}</span>)}
            </div>
          )}
          {hasDateRange && (
            <div style={{ marginBottom: 4, opacity: 0.5 }}>
              Date: {rules.date_after || '∞'} → {rules.date_before || '∞'}
            </div>
          )}

          <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
            <button onClick={onEdit}
              style={{ flex: 1, fontSize: 11, padding: '5px 0', borderRadius: 4, cursor: 'pointer',
                background: 'rgba(255,255,255,0.06)', color: '#ccc', border: '1px solid rgba(255,255,255,0.1)',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
              <IoCreateOutline /> Edit
            </button>
            <button onClick={onDelete}
              style={{ flex: 1, fontSize: 11, padding: '5px 0', borderRadius: 4, cursor: 'pointer',
                background: 'rgba(255,80,80,0.08)', color: '#f66', border: '1px solid rgba(255,80,80,0.2)',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
              <IoTrashOutline /> Delete
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
